import { TranslationsContext } from '@teliaee/telia-translate';
import { format } from 'date-fns';
import { inject, observer } from 'mobx-react';
import React from 'react';
import {
  AnalyticsTrigger,
  Button,
  Card,
  CardContent,
  EqualHeight,
  Grid,
  GridColumn,
  Heading,
  Icon,
  ITextFieldProps,
  Notice,
  Separator,
  Spinner,
  TextField,
  THeadingLevel,
} from 'telia-front-react';
import Config from '../../../Config';
import DiscountCodeStore from '../../../stores/DiscountCodeStore';
import { FetchStatus } from '../../../util/FetchStatus';
import { OfferActions } from '../../OfferInterfaces';

interface B2BLoyaltyDiscountCodeCardProps {
  recommendationItemId: number;
  salesCampaignId: number;
  offerActions?: OfferActions;
  headingLevel?: THeadingLevel;
  discountCodeStore?: DiscountCodeStore;
}

const B2BLoyaltyDiscountCodeCard = (props: B2BLoyaltyDiscountCodeCardProps): JSX.Element => {
  const { translate } = React.useContext(TranslationsContext);
  const { recommendationItemId, salesCampaignId, offerActions, headingLevel, discountCodeStore } = props;
  const [isCopied, setIsCopied] = React.useState(false);
  const { redeemed, used, lastUsedOrder, discountCode, isUsable, fetchStatus } = discountCodeStore.getDiscountCodeData(salesCampaignId, recommendationItemId);

  React.useEffect(() => {
    discountCodeStore.fetchDiscountCode(salesCampaignId, recommendationItemId);
  }, []);

  const getCodeField = () => {
    const textFieldProps: ITextFieldProps = {
      type: 'text',
      id: 'shop-discount-code',
      label: translate('discountCode.code'),
      successMessage: isCopied ? translate('discountCode.copied') : undefined,
      defaultValue: discountCode,
      isReadOnly: true,
      inputStyle: {
        fontWeight: 'bold',
      },
      isDisabled: !isUsable,
    };

    if (fetchStatus === FetchStatus.ERROR) {
      return (
        <Notice size="small" type="error">
          {translate('discountCode.error')}
        </Notice>
      );
    }
    if (fetchStatus === FetchStatus.FETCHING) {
      return <Spinner />;
    }
    if (redeemed && !used) {
      return (
        <Grid gutter="small" noWrap={true} align={['bottom-xs']}>
          <GridColumn width={['max']}>
            <TextField {...textFieldProps} />
          </GridColumn>
          <GridColumn width={['min']}>
            <AnalyticsTrigger
              trigger={{
                id: 'Link - Telia Äriklass - Kopeeri sooduskood',
                callback: 'onClick',
              }}
            >
              <Button
                text={translate('discountCode.copy')}
                icon="#copy"
                onClick={(event) => {
                  event.preventDefault();
                  navigator.clipboard.writeText(discountCode);
                  setIsCopied(true);
                }}
              />
            </AnalyticsTrigger>
          </GridColumn>
        </Grid>
      );
    }
    if (redeemed && used) {
      return <TextField {...textFieldProps} isDisabled={true} />;
    }
    return null;
  };

  const getContent = () => {
    if (used) {
      return (
        <>
          {translate('discountCode.used')} <strong>{format(new Date(lastUsedOrder.dateTime), `dd.MM.yyyy '${translate('discountCode.time')}' HH.mm`)}</strong>.
        </>
      );
    }
    if (redeemed) {
      return translate('personalOffers.b2b.loyalty.content.discountCode.redeemed');
    }
    return translate('personalOffers.b2b.loyalty.content.discountCode');
  };

  return (
    <Card background="neutral-bg-level-1">
      <CardContent>
        <EqualHeight group="heading">
          <Icon size="sm" modifiers="#offering" />
          <Heading className="h4" level={headingLevel}>
            {translate('personalOffers.b2b.loyalty.title.discountCode')}
          </Heading>
          <Separator spacing="xxsmall" color="invisible" />
        </EqualHeight>
        <EqualHeight group="content">
          {getContent()}
          {(redeemed || used || fetchStatus === FetchStatus.ERROR) && <Separator spacing="xsmall" color="invisible" />}
          {getCodeField()}
        </EqualHeight>
        <EqualHeight group="cta-button">
          <Separator spacing="xsmall" color="invisible" />
          <AnalyticsTrigger
            trigger={{
              id: `Button - Telia Äriklass - ${redeemed ? 'E-poodi' : 'Vaatan sooduskoodi'}`,
              callback: 'onClick',
            }}
          >
            <Button
              text={translate(`personalOffers.b2b.loyalty.cta.text.${redeemed ? 'toShop' : 'discountCode'}`)}
              disabled={!redeemed && fetchStatus === FetchStatus.FETCHING}
              iconRight="#arrow-right"
              block={true}
              variant="secondary"
              url={redeemed ? Config.teliaShopUrl : undefined}
              onClick={(event) => {
                if (!redeemed) {
                  event.preventDefault();
                  offerActions.accept(recommendationItemId);
                  discountCodeStore.createDiscountCode(salesCampaignId, recommendationItemId);
                }
              }}
            />
          </AnalyticsTrigger>
        </EqualHeight>
      </CardContent>
    </Card>
  );
};

export default inject('discountCodeStore')(observer(B2BLoyaltyDiscountCodeCard));
